import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Plus, Pencil, Trash2, Crosshair, Sparkles, Loader2, Target, Lightbulb, ShieldAlert, TrendingUp, AlertTriangle } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const emptyForm = {
  company_id: "", name: "", platforms: [], website: "", instagram_handle: "",
  facebook_page: "", industry: "", strengths: "", weaknesses: "",
  content_style: "", target_audience: "", notes: "",
};

export default function CompetitorPage() {
  const [competitors, setCompetitors] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [filterCompany, setFilterCompany] = useState("all");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [analyzing, setAnalyzing] = useState(false);
  const [analysis, setAnalysis] = useState(null);
  const [analyses, setAnalyses] = useState([]);
  const [showAnalysis, setShowAnalysis] = useState(null);
  const { t } = useLanguage();

  const load = async () => {
    try {
      const [comp, co] = await Promise.all([
        axios.get(`${API}/competitors`, {
          params: filterCompany !== "all" ? { company_id: filterCompany } : {},
          withCredentials: true,
        }),
        axios.get(`${API}/companies`, { withCredentials: true }),
      ]);
      setCompetitors(comp.data);
      setCompanies(co.data);
    } catch (e) { console.error(e); }
  };

  const loadAnalyses = async (companyId) => {
    try {
      const res = await axios.get(`${API}/competitors/analyses`, {
        params: { company_id: companyId }, withCredentials: true,
      });
      setAnalyses(res.data);
    } catch (e) { console.error(e); }
  };

  useEffect(() => { load(); }, [filterCompany]);

  const handleSave = async () => {
    try {
      if (editing) {
        await axios.put(`${API}/competitors/${editing}`, form, { withCredentials: true });
        toast.success("Wettbewerber aktualisiert");
      } else {
        await axios.post(`${API}/competitors`, form, { withCredentials: true });
        toast.success("Wettbewerber hinzugefuegt");
      }
      setOpen(false);
      setEditing(null);
      setForm(emptyForm);
      load();
    } catch (e) { toast.error("Fehler"); }
  };

  const handleEdit = (c) => {
    setEditing(c.id);
    setForm({ ...emptyForm, ...c });
    setOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Wettbewerber loeschen?")) return;
    try {
      await axios.delete(`${API}/competitors/${id}`, { withCredentials: true });
      toast.success("Geloescht");
      load();
    } catch { toast.error("Fehler"); }
  };

  const handleAnalyze = async (companyId) => {
    setAnalyzing(true);
    setAnalysis(null);
    try {
      const res = await axios.post(`${API}/competitors/analyze`, { company_id: companyId }, { withCredentials: true });
      setAnalysis(res.data);
      loadAnalyses(companyId);
      toast.success("Wettbewerbsanalyse abgeschlossen!");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Analyse fehlgeschlagen");
    }
    setAnalyzing(false);
  };

  const companyName = (id) => companies.find((c) => c.id === id)?.name || "Unbekannt";

  const field = (key, label, type = "input") => (
    <div>
      <Label className="text-xs text-[var(--studio-text-muted)]">{label}</Label>
      {type === "textarea" ? (
        <Textarea value={form[key] || ""} onChange={(e) => setForm((f) => ({ ...f, [key]: e.target.value }))} className="mt-1" rows={2} />
      ) : (
        <Input value={form[key] || ""} onChange={(e) => setForm((f) => ({ ...f, [key]: e.target.value }))} className="mt-1" />
      )}
    </div>
  );

  const currentAnalysis = analysis?.analysis || (showAnalysis ? analyses.find((a) => a.id === showAnalysis)?.analysis : null);

  return (
    <div data-testid="competitor-page" className="space-y-6 max-w-6xl">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-4xl font-bold font-heading tracking-tight">{t("competitors.title")}</h1>
          <p className="text-[var(--studio-text-muted)] mt-1">{t("competitors.subtitle")}</p>
        </div>
        <div className="flex items-center gap-3">
          <Select value={filterCompany} onValueChange={(v) => { setFilterCompany(v); if (v !== "all") loadAnalyses(v); }}>
            <SelectTrigger className="w-48" data-testid="comp-company-filter">
              <SelectValue placeholder="Alle Firmen" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Alle Firmen</SelectItem>
              {companies.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}
            </SelectContent>
          </Select>
          <Dialog open={open} onOpenChange={(v) => { setOpen(v); if (!v) { setEditing(null); setForm(emptyForm); } }}>
            <DialogTrigger asChild>
              <Button data-testid="add-competitor-btn" className="bg-[#2563EB] hover:bg-[#1D4ED8] text-white">
                <Plus className="w-4 h-4 mr-2" /> Wettbewerber
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle className="font-heading">{editing ? "Wettbewerber bearbeiten" : "Neuer Wettbewerber"}</DialogTitle>
              </DialogHeader>
              <div className="grid grid-cols-2 gap-4 mt-4">
                <div>
                  <Label className="text-xs text-[var(--studio-text-muted)]">Fuer Firma *</Label>
                  <Select value={form.company_id} onValueChange={(v) => setForm((f) => ({ ...f, company_id: v }))}>
                    <SelectTrigger className="mt-1"><SelectValue placeholder="Firma" /></SelectTrigger>
                    <SelectContent>{companies.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                {field("name", "Name des Wettbewerbers *")}
                {field("industry", "Branche")}
                {field("website", "Website")}
                {field("instagram_handle", "Instagram Handle")}
                {field("facebook_page", "Facebook Page")}
                {field("strengths", "Staerken", "textarea")}
                {field("weaknesses", "Schwaechen", "textarea")}
                {field("content_style", "Content-Stil")}
                {field("target_audience", "Zielgruppe")}
              </div>
              <div className="mt-4">{field("notes", "Notizen / Beobachtungen", "textarea")}</div>
              <div className="flex justify-end gap-2 mt-6">
                <Button variant="outline" onClick={() => { setOpen(false); setEditing(null); setForm(emptyForm); }}>Abbrechen</Button>
                <Button onClick={handleSave} data-testid="save-competitor-btn" className="bg-[#2563EB] hover:bg-[#1D4ED8] text-white"
                  disabled={!form.name || !form.company_id}>
                  {editing ? "Aktualisieren" : "Hinzufuegen"}
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Analyze Button */}
      {filterCompany !== "all" && competitors.length > 0 && (
        <Card className="border-[#2563EB]/20 shadow-none bg-[#2563EB]/[0.02]">
          <CardContent className="p-5 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <Target className="w-6 h-6 text-[#2563EB]" />
              <div>
                <p className="font-medium font-heading">KI-Wettbewerbsanalyse</p>
                <p className="text-xs text-[var(--studio-text-muted)]">
                  {competitors.length} Wettbewerber fuer {companyName(filterCompany)} analysieren
                </p>
              </div>
            </div>
            <Button onClick={() => handleAnalyze(filterCompany)} disabled={analyzing}
              data-testid="analyze-competitors-btn" className="bg-[#2563EB] hover:bg-[#1D4ED8] text-white">
              {analyzing ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Sparkles className="w-4 h-4 mr-2" />}
              {analyzing ? "Wird analysiert..." : "Analyse starten"}
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Analysis Results */}
      {currentAnalysis && (
        <div className="space-y-4 animate-fade-in" data-testid="analysis-results">
          {/* Market Overview */}
          <Card className="border-[var(--studio-border)] shadow-none bg-[#0F0F0F] text-white">
            <CardContent className="p-5">
              <p className="font-heading font-semibold mb-2 flex items-center gap-2">
                <TrendingUp className="w-4 h-4 text-[#2563EB]" /> Marktuebersicht
              </p>
              <p className="text-sm text-white/80">{currentAnalysis.market_overview}</p>
            </CardContent>
          </Card>

          {/* Competitor Analysis */}
          {currentAnalysis.competitor_analysis?.length > 0 && (
            <Card className="border-[var(--studio-border)] shadow-none">
              <CardHeader><CardTitle className="font-heading text-base">Wettbewerber-Analyse</CardTitle></CardHeader>
              <CardContent className="space-y-3">
                {currentAnalysis.competitor_analysis.map((ca, i) => (
                  <div key={i} className="p-4 rounded-lg bg-[var(--studio-bg)]">
                    <p className="font-medium text-sm mb-2">{ca.name}</p>
                    <div className="grid grid-cols-3 gap-3 text-xs">
                      <div>
                        <p className="text-[var(--studio-text-muted)] font-medium mb-1">Wahrscheinlicher Fokus</p>
                        <p>{ca.likely_content_focus}</p>
                      </div>
                      <div>
                        <p className="text-green-700 font-medium mb-1">Luecke</p>
                        <p>{ca.gap}</p>
                      </div>
                      <div>
                        <p className="text-[#2563EB] font-medium mb-1">Differenzierung</p>
                        <p>{ca.differentiation}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </CardContent>
            </Card>
          )}

          {/* Content Opportunities */}
          {currentAnalysis.content_opportunities?.length > 0 && (
            <Card className="border-[var(--studio-border)] shadow-none">
              <CardHeader>
                <CardTitle className="font-heading text-base flex items-center gap-2">
                  <Lightbulb className="w-4 h-4 text-amber-500" /> Differenzierende Content-Ideen
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                {currentAnalysis.content_opportunities.map((opp, i) => (
                  <div key={i} className="p-4 rounded-lg border border-[var(--studio-border)] hover:shadow-sm transition-shadow" data-testid={`opportunity-${i}`}>
                    <div className="flex items-center gap-2 mb-2">
                      <p className="font-medium text-sm">{opp.title}</p>
                      <Badge variant="outline" className="text-[10px] capitalize">{opp.type}</Badge>
                      <Badge className={`text-[10px] border-0 ${
                        opp.urgency === "hoch" ? "bg-red-100 text-red-800" :
                        opp.urgency === "mittel" ? "bg-amber-100 text-amber-800" : "bg-green-100 text-green-800"
                      }`}>{opp.urgency}</Badge>
                    </div>
                    <p className="text-xs text-[var(--studio-text-muted)]">{opp.description}</p>
                    {opp.competitive_advantage && (
                      <p className="text-xs mt-2 text-green-700 flex items-center gap-1">
                        <TrendingUp className="w-3 h-3" /> {opp.competitive_advantage}
                      </p>
                    )}
                    {opp.why_now && (
                      <p className="text-xs mt-1 text-[var(--studio-text-muted)] italic">{opp.why_now}</p>
                    )}
                  </div>
                ))}
              </CardContent>
            </Card>
          )}

          {/* Tips & Avoid */}
          <div className="grid grid-cols-2 gap-4">
            {currentAnalysis.positioning_tips?.length > 0 && (
              <Card className="border-[var(--studio-border)] shadow-none">
                <CardHeader><CardTitle className="font-heading text-base text-green-700">Positionierungs-Tipps</CardTitle></CardHeader>
                <CardContent>
                  <ul className="space-y-2">
                    {currentAnalysis.positioning_tips.map((tip, i) => (
                      <li key={i} className="text-xs flex items-start gap-2">
                        <TrendingUp className="w-3 h-3 text-green-600 mt-0.5 flex-shrink-0" /> {tip}
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            )}
            {currentAnalysis.avoid?.length > 0 && (
              <Card className="border-[var(--studio-border)] shadow-none">
                <CardHeader><CardTitle className="font-heading text-base text-red-700">Vermeiden</CardTitle></CardHeader>
                <CardContent>
                  <ul className="space-y-2">
                    {currentAnalysis.avoid.map((a, i) => (
                      <li key={i} className="text-xs flex items-start gap-2">
                        <AlertTriangle className="w-3 h-3 text-red-500 mt-0.5 flex-shrink-0" /> {a}
                      </li>
                    ))}
                  </ul>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      )}

      {/* Previous Analyses */}
      {analyses.length > 0 && !currentAnalysis && (
        <Card className="border-[var(--studio-border)] shadow-none">
          <CardHeader><CardTitle className="font-heading text-base">Fruehere Analysen</CardTitle></CardHeader>
          <CardContent className="space-y-2">
            {analyses.map((a) => (
              <button key={a.id} onClick={() => setShowAnalysis(a.id)} data-testid={`prev-analysis-${a.id}`}
                className="w-full text-left p-3 rounded-lg bg-[var(--studio-bg)] hover:bg-[var(--studio-border)] transition-colors text-sm">
                <p className="font-medium">{new Date(a.created_at).toLocaleDateString("de-CH", { day: "2-digit", month: "2-digit", year: "numeric" })} &middot; {a.competitor_count} Wettbewerber</p>
                <p className="text-xs text-[var(--studio-text-muted)]">{a.season?.season} - {a.season?.month_name}</p>
              </button>
            ))}
          </CardContent>
        </Card>
      )}

      {/* Competitor List */}
      <div>
        <h2 className="text-lg font-semibold font-heading mb-3">Wettbewerber ({competitors.length})</h2>
        {competitors.length === 0 ? (
          <Card className="border-[var(--studio-border)] shadow-none">
            <CardContent className="py-16 text-center">
              <Crosshair className="w-12 h-12 mx-auto text-[var(--studio-text-muted)] opacity-40 mb-4" />
              <p className="text-[var(--studio-text-muted)]">Noch keine Wettbewerber angelegt.</p>
              <p className="text-sm text-[var(--studio-text-muted)] mt-1">Fuege Wettbewerber hinzu fuer KI-gestuetzte Analyse.</p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid gap-3">
            {competitors.map((c) => (
              <Card key={c.id} className="border-[var(--studio-border)] shadow-none" data-testid={`competitor-card-${c.id}`}>
                <CardContent className="p-4 flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="font-medium">{c.name}</h3>
                      {c.industry && <Badge variant="secondary" className="text-xs">{c.industry}</Badge>}
                    </div>
                    <p className="text-xs text-[var(--studio-text-muted)]">{companyName(c.company_id)}</p>
                    <div className="flex flex-wrap gap-3 mt-2 text-xs text-[var(--studio-text-muted)]">
                      {c.instagram_handle && <span>IG: {c.instagram_handle}</span>}
                      {c.website && <span>{c.website}</span>}
                    </div>
                    {c.content_style && <p className="text-xs mt-1">Stil: {c.content_style}</p>}
                    {c.strengths && <p className="text-xs text-green-700 mt-1">Staerken: {c.strengths}</p>}
                    {c.weaknesses && <p className="text-xs text-red-600 mt-1">Schwaechen: {c.weaknesses}</p>}
                  </div>
                  <div className="flex gap-1">
                    <Button variant="ghost" size="icon" onClick={() => handleEdit(c)} data-testid={`edit-comp-${c.id}`}>
                      <Pencil className="w-4 h-4" />
                    </Button>
                    <Button variant="ghost" size="icon" onClick={() => handleDelete(c.id)} data-testid={`delete-comp-${c.id}`}>
                      <Trash2 className="w-4 h-4 text-red-500" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
